import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useQuery} from '@tanstack/react-query'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {
    useAccessToken,
    useShopperBasketsMutation,
    useShopperContext,
    useShopperContextsMutation,
    useUsid,
    useCustomerProductLists,
    useShopperCustomersMutation,
    useDNT,
} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'

// Chakra
import {Box} from '@chakra-ui/react'
import {SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'

// Cookies
import {CookiesProvider} from 'react-cookie'

// Layout
import {Footer, Header, NewsletterModalSmall} from '_uiComponents'

// Hooks
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAnalytics, useCustomApi, useCurrentBasket, useStickyChatPosition} from '_customHooks'
import {useSearchParams} from '@salesforce/retail-react-app/app/hooks'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations, parseMetaTags, RawHtmlInjector, normalizeLocaleForIntl} from '_utils'
import {DEFAULT_LOCALE, ACTIVE_DATA_ENABLED} from '@salesforce/retail-react-app/app/constants'
import {DEFAULT_SITE_TITLE, MAX_WISHLIST_COUNT, THEME_COLOR} from '_utils/data/constants'

import Seo from '@salesforce/retail-react-app/app/components/seo'
import {Helmet} from 'react-helmet'
import {DrawerProvider, ModalProvider, LoaderProvider, useHasLocaleError, AccessibilityProvider, GlobalConfigProvider, HeaderProvider} from '_context'
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {WishlistType, BasketModel} from '_types'

import {HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'

import {hashValueHex} from '_analytics/utils/generic-utils'

// Extend the Window interface to include the _hmt property
declare global {
    interface Window {
        _hmt?: object[]
        OnetrustActiveGroups?: string
        OneTrust?: {
            OnConsentChanged?: (callback: () => void) => void
        }
        ApplePaySession?: {
            canMakePayments: () => boolean
        }
        enableUsableNetAssistive?: object
        DYO?: {
            dyhash?: {
                sha256?: (value: string) => string
            }
            ActiveConsent?: {
                updateConsentAcceptedStatus?: (accepted: boolean) => void
            }
            CookiesAPI?: {
                get?: (key: string) => string | undefined
            }
        }
    }
}

interface AppProps {
    children: React.ReactNode
}

const isClient = typeof window !== 'undefined'

const App = (props: AppProps) => {
    const {children} = props

    // Keeps the cross-origin Salesforce chat bubble positioned above the active sticky section
    // (class `cc-sticky-section`) on desktop and mobile. Mounted once here; renders nothing.
    useStickyChatPosition()

    const {getTokenWhenReady} = useAccessToken()
    const {analyticsUserData} = useAnalytics()
    const appOrigin = useAppOrigin()
    const location = useLocation()
    const {site, locale} = useMultiSite()
    const {customParams} = getConfig()
    const firstRender = useRef<boolean>(true)
    const previousPathname = useRef<string | undefined>(undefined)
    const lastDyCartSyncRef = useRef<string | null>(null)
    const isDyApiReadyRef = useRef(false)
    const basketRef = useRef<BasketModel | null>(null)
    const hasLocaleError = useHasLocaleError()
    const [searchParams] = useSearchParams()

    /**
     * Feature-detects a dynamic bottom browser bar (iOS 26-style "Liquid Glass" and any browser with
     * the same collapsing-bar pattern) by comparing the large viewport (100lvh, bar retracted) to the
     * small viewport (100svh, bar present). This is intentionally NOT device/OS detection: on
     * desktop, tablet, Android and "old" Safari (e.g. iOS < 26, which has no translucent bar) the two
     * units match and this stays false. Only when true do we render the `safari-bar-shim` (see the
     * `.has-dynamic-bottom-bar .safari-bar-shim` rule in globalStyles), so the shim's white strip
     * never shows above the classic opaque toolbar on browsers that don't need it.
     */
    const [hasDynamicBottomBar, setHasDynamicBottomBar] = useState(false)
    // Firefox for Android has the same problem as iOS 26 Safari: a translucent dynamic bottom toolbar
    // that samples the page content behind it, so scrolling content shows through. Handled with the
    // SAME white shim, but detected/scoped on a SEPARATE branch so the iOS/Safari handling below is
    // never touched. The two branches are mutually exclusive by engine: Firefox does not support
    // `-webkit-touch-callout` (so the WebKit CSS/detection stays inert on it) and WebKit does not
    // support `-moz-appearance` (so the Firefox branch stays inert on Safari).
    const [hasDynamicBottomBarFirefox, setHasDynamicBottomBarFirefox] = useState(false)

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return

        const measure = (unit: string): number => {
            const probe = document.createElement('div')
            probe.style.cssText = `position:fixed;top:0;left:0;height:${unit};width:0;visibility:hidden;pointer-events:none`
            document.body.appendChild(probe)
            const height = probe.getBoundingClientRect().height
            probe.remove()
            return height
        }

        // The shim only makes sense on the iOS 26+ *translucent* "Liquid Glass" bottom bar. A large
        // `100lvh − 100svh` gap alone is NOT enough: older iOS (e.g. iPhone X / iOS 16) also has a
        // collapsing toolbar with a big gap, but it is OPAQUE and needs no shim — there the shim's
        // ~5px would show as a white strip. So we require three conditions together:
        //  1. WebKit/Safari (the shim CSS is scoped to it anyway);
        //  2. a dynamic bottom bar (collapsing toolbar → large lvh/svh gap);
        //  3. Safari 26+, inferred via a capability introduced in Safari 26 (CSS anchor positioning),
        //     since the iOS 26 user-agent is frozen and cannot be parsed reliably.
        // iPhone X / iOS 16 satisfies 1 and 2 but fails 3 → no class → no white strip.
        const isWebKit = CSS.supports('-webkit-touch-callout', 'none')
        const isSafari26Plus = CSS.supports('anchor-name', '--x')
        const hasDynamicBar = measure('100lvh') - measure('100svh') > 16

        setHasDynamicBottomBar(isWebKit && isSafari26Plus && hasDynamicBar)

        // Firefox-only branch (Firefox Android's translucent dynamic bottom bar). `-moz-appearance` is
        // a Firefox capability (absent in WebKit/Blink), so this never overlaps the WebKit branch above.
        // The dynamic-bar gate (lvh/svh gap) keeps it off desktop Firefox and any Firefox without a
        // collapsing bar, so the shim's white strip only shows where the translucent bar actually is.
        const isFirefox = CSS.supports('-moz-appearance', 'none')

        setHasDynamicBottomBarFirefox(isFirefox && hasDynamicBar)
    }, [])
    const {updateDnt} = useDNT()
    const didUpdateDntRef = useRef(false)
    const getConsentTrackingValue = useCallback((): string => {
        if (typeof document === 'undefined') return ''
        const cookies = document.cookie.split(';').reduce(
            (acc, cookieStr) => {
                const [key, ...valArr] = cookieStr.trim().split('=')
                acc[key] = valArr.join('=')
                return acc
            },
            {} as Record<string, string>,
        )

        if (cookies.OptanonConsent) {
            const optanon = decodeURIComponent(cookies.OptanonConsent)
            const groupsMatch = optanon.match(/groups=([^&]+)/)
            if (groupsMatch) {
                const groupsArray = groupsMatch[1].split(',')
                const consentCategoryGroup = groupsArray.find((group) => group.indexOf('C0004:') === 0)
                if (consentCategoryGroup) {
                    return consentCategoryGroup.split(':')[1]
                }
            }
        }
        return ''
    }, [])
    const updateDntAfterTokenReady = useCallback(async () => {
        await getTokenWhenReady()
        await updateDnt(getConsentTrackingValue() !== '1')
    }, [getTokenWhenReady, updateDnt, getConsentTrackingValue])

    useEffect(() => {
        if (didUpdateDntRef.current) return

        let canceled = false
        const updateDntAfterTokenReady = async () => {
            await getTokenWhenReady()
            if (canceled || didUpdateDntRef.current) return
            didUpdateDntRef.current = true
            await updateDnt(getConsentTrackingValue() !== '1')
        }

        void updateDntAfterTokenReady()

        return () => {
            canceled = true
        }
    }, [getTokenWhenReady, getConsentTrackingValue, updateDnt])

    useEffect(() => {
        if (typeof window === 'undefined') return

        const setDyConsent = () => {
            if (window.DY && window.DYO?.ActiveConsent?.updateConsentAcceptedStatus) {
                const groups = window?.OnetrustActiveGroups || ''
                let profilingAccepted = false
                if (groups.length > 0) {
                    profilingAccepted = groups.includes('C0004')
                } else {
                    profilingAccepted = getConsentTrackingValue() === '1'
                }

                window.DYO.ActiveConsent.updateConsentAcceptedStatus(profilingAccepted)
                window.DY.userActiveConsent = {accepted: profilingAccepted}

                if (profilingAccepted && window.DYO?.CookiesAPI?.get) {
                    const dyidValue = window.DYO.CookiesAPI.get('_dyid')
                    if (dyidValue) {
                        document.cookie = `_dyid_server=${dyidValue}; path=/; Max-Age=31536000; Secure`
                    }
                } else {
                    document.cookie = '_dyid_server=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;'
                }
            }
        }

        const onConsentChanged = () => {
            void updateDntAfterTokenReady()
            setDyConsent()
        }

        if (window?.OneTrust?.OnConsentChanged) {
            window.OneTrust.OnConsentChanged(onConsentChanged)
            return
        }

        const intervalId = window.setInterval(() => {
            if (window?.OneTrust?.OnConsentChanged) {
                window.clearInterval(intervalId)
                window.OneTrust.OnConsentChanged(onConsentChanged)
            }
        }, 1000)

        return () => {
            window.clearInterval(intervalId)
        }
    }, [updateDntAfterTokenReady, getConsentTrackingValue])

    const searchParamId = searchParams.id as string
    const wishlistType: WishlistType = searchParamId ? 'public' : 'private'

    if (hasLocaleError) {
        throw new HTTPNotFound('Locale code not found.')
    }

    if (hasLocaleError) {
        throw new HTTPNotFound('Locale code not found.')
    }

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n,
    })
    const intlLocale = normalizeLocaleForIntl(targetLocale)

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(location?.pathname)
    // DY client side script
    const dynamicYieldConfig = customParams?.dynamicYieldConfig
    const dynamicYieldSiteConfig = dynamicYieldConfig?.[site.id]
    const dynamicYieldInstanceId = dynamicYieldSiteConfig?.instanceId
    const hasDynamicYieldConfig = Boolean(dynamicYieldInstanceId)
    const isDyEu = dynamicYieldSiteConfig?.dataCenter === 'eu'
    const dyHosts = isDyEu
        ? {
              cdn: '//cdn-eu.dynamicyield.com',
              st: '//st-eu.dynamicyield.com',
              rcom: '//rcom-eu.dynamicyield.com',
          }
        : {
              cdn: '//cdn.dynamicyield.com',
              st: '//st.dynamicyield.com',
              rcom: '//rcom.dynamicyield.com',
          }

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale, appOrigin)
        },
        enabled: isServer,
    })

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current customer.
    const {data: customer, refetch: refetchCustomer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const {useCustomPreference} = useCustomApi()
    const {data: appPreferences} = useCustomPreference([
        'isBaiduAnalysisEnabled',
        'baiduSiteVerification',
        'ACCESSIBILITY_USABLENET_JS',
        'AdditionalMetaTags',
        'apikeyv3_recaptcha',
        'pwaLiveChatScript',
        'PAYPAL_EXPRESS_Disable_Funds',
        'PAYPAL_EXPRESS_Allowed_Currencies',
        'PP_API_PaymentAction',
        'PP_API_Button_Location',
        'paypal_clientId',
        'paypal_button_style',
        'ShippingMethod_NI',
        'Allowed_Suretax_Dispatch',
        'ApplePay_Enabled',
        'ApplePay_MerchantId',
        'ApplePay_MerchantName',
        'ApplePay_MerchantCapabilities',
        'ApplePay_SupportedNetworks',
        'hostLink',
        'CountriesWithZeroPrices',
        'PhoneLogin',
        'customNumberContact',
        'whatsAppEnabledCountries',
        'Catalog_ManName',
        'enableSocialShare',
        'TencentCaptchaScriptUrl',
        'TencentCaptchaAppID',
        'disableEditorialWidget',
        'CloudinaryImageTransformationsDPR',
        'CloudinaryImageTransformationsFormat',
        'CloudinaryImageTransformationsQuality',
        'CloudinaryImageTransformations',
        'CloudinaryVideoTransformationsQuality',
        'CloudinaryVideoTransformationsBitRate',
        'CloudinaryVideoTransformations',
        'CloudinaryVideoFormat',
        'CloudinaryVideoPosterTransformations',
        'CloudinaryPageDesignerCNAME',
        'emailRegexFormValidation',
    ])
    const parsedMetaTags = parseMetaTags(appPreferences?.AdditionalMetaTags)

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')
    const updateShippingAddressForShipment = useShopperBasketsMutation('updateShippingAddressForShipment')

    const createCustomerProductList = useShopperCustomersMutation('createCustomerProductList')

    /**
     * Creates a new wishlist.
     * @param {WishlistType} type - Wishlist type.
     * @returns {Promise<void>} A promise that resolves when wishlist is created.
     */
    const createWishlist = async (type: WishlistType): Promise<void> => {
        if (customer.customerId) {
            await createCustomerProductList.mutateAsync({
                parameters: {customerId: customer.customerId},
                body: {type: 'wish_list', public: type === 'public'},
            })
        }
    }

    /**
     * Handles the creation of public and private wishlists.
     */
    useCustomerProductLists(
        {
            parameters: {customerId: customer.customerId},
        },
        {
            onSuccess: async (result) => {
                const wishlists = result?.data?.filter((list) => list.type === 'wish_list') || []

                const privateWishlistExists = wishlists.some((list) => list.public === false)
                const publicWishlistExists = wishlists.some((list) => list.public === true)

                if (wishlists.length === 0 && !privateWishlistExists && !publicWishlistExists) {
                    await createWishlist('private')
                    await createWishlist('public')
                } else if (wishlists.length === 1 && (!privateWishlistExists || !publicWishlistExists)) {
                    if (!privateWishlistExists) {
                        await createWishlist('private')
                    }

                    if (!publicWishlistExists) {
                        await createWishlist('public')
                    }
                }

                if (wishlists.length > MAX_WISHLIST_COUNT) {
                    console.error(`Wishlist creation issue happened: this device has ${result.total} wishlists (more than ${MAX_WISHLIST_COUNT}).`)
                }
            },
            enabled: isClient && wishlistType === 'private' && !!customer.customerId,
        },
    )

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency && basket?.basketId) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency},
            })
        }
    }, [basket?.currency, currency])

    useEffect(() => {
        basketRef.current = basket || null
    }, [basket])

    useEffect(() => {
        // update the basket customer email
        if (basket && customer?.isRegistered && basket.basketId) {
            if (customer.email && customer.email !== basket.customerInfo?.email) {
                updateCustomerForBasket.mutate({
                    parameters: {basketId: basket.basketId},
                    body: {
                        email: customer.email,
                    },
                })
            }

            // update basket's default shipping address fields.
            const basketShippingAddress = basket.shipments?.[0]?.shippingAddress
            const setPhone = customer.phoneMobile && !basketShippingAddress?.phone
            const setFirstName = customer.firstName && !basketShippingAddress?.firstName
            const setLastName = customer.lastName && !basketShippingAddress?.lastName

            if (setPhone || setFirstName || setLastName) {
                updateShippingAddressForShipment.mutate({
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: 'me',
                    },
                    body: {
                        ...(setFirstName && {firstName: customer.firstName}),
                        ...(setLastName && {lastName: customer.lastName}),
                        ...(setPhone && {phone: customer.phoneMobile}),
                    },
                })
            }
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email, customer?.phoneMobile])

    const sendDySyncCart = useCallback((basketToSync: BasketModel | null) => {
        if (!basketToSync?.productItems || basketToSync.productItems.length === 0) {
            return
        }
        if (typeof window === 'undefined' || !isDyApiReadyRef.current || !window.DY || typeof window.DY.API !== 'function') {
            return
        }

        const cart = basketToSync.productItems.map((item) => ({
            productId: item.productId || item.itemId || '',
            quantity: item.quantity ?? 0,
            itemPrice: item.c_price?.price?.value ?? 0,
        }))

        const value = basketToSync.c_subtotal?.value ?? cart.reduce((sum, item) => sum + (item.itemPrice || 0) * (item.quantity || 0), 0)

        const payload = {
            dyType: 'sync-cart-v1',
            value,
            currency: basketToSync.currency,
            cart,
        }

        const signature = JSON.stringify(payload)
        if (signature === lastDyCartSyncRef.current) {
            return
        }
        lastDyCartSyncRef.current = signature

        try {
            window.DY.API('event', {
                name: 'Sync cart',
                properties: payload,
            })
        } catch (error) {
            console.error('[DY Tracking] Errore nel tracking sync cart:', error)
        }
    }, [])

    const registerDyReadyCallback = useCallback(() => {
        if (typeof window === 'undefined' || !window.DY || typeof window.DY.API !== 'function') {
            return
        }

        window.DY.API('callback', () => {
            isDyApiReadyRef.current = true
            sendDySyncCart(basketRef.current)
        })
        if (window.DY && window.DY.userActiveConsent === undefined) {
            const accepted = getConsentTrackingValue() === '1'
            window.DY.userActiveConsent = {accepted}
        }
    }, [sendDySyncCart])

    useEffect(() => {
        if (!hasDynamicYieldConfig) {
            return
        }

        if (window.DY && typeof window.DY.API === 'function') {
            registerDyReadyCallback()
        }
    }, [hasDynamicYieldConfig, registerDyReadyCallback])

    useEffect(() => {
        sendDySyncCart(basketRef.current)
    }, [basket, sendDySyncCart])

    useEffect(() => {
        // Listen for online status changes.
        if (isClient) {
            const body = document.querySelector('body')
            window.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    body?.classList.remove('no-focus-outline')
                }
            })
            window.addEventListener('mousedown', () => {
                if (!body?.classList.contains('no-focus-outline')) {
                    body?.classList.add('no-focus-outline')
                }
            })
            body?.classList.add('no-focus-outline')
        }
    }, [])

    // Handle updating the shopper context
    const {usid, getUsidWhenReady} = useUsid()
    const {data: shopperContext, isLoading: isShopperContextLoading} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {
            enabled: !isServer && !!usid,
        },
    )
    const {mutateAsync: createShopperContextMutation} = useShopperContextsMutation('createShopperContext')
    const {mutateAsync: updateShopperContextMutation} = useShopperContextsMutation('updateShopperContext')

    useEffect(() => {
        // This logic creates a context for the user in which,
        // if they have accepted the OneTrust cookies
        // they belong to the customer group optAccepted_only
        const consentCategoryValue = getConsentTrackingValue()
        const createShopperContext = async () => {
            const _usid = await getUsidWhenReady()
            let body = {}
            if (consentCategoryValue === '1') {
                body = {
                    customQualifiers: {
                        optAccepted: consentCategoryValue,
                    },
                }
                void updateDnt(false)
                await getTokenWhenReady()
            }
            await createShopperContextMutation({
                parameters: {usid: _usid, siteId: site.id, evaluateContextWithClientIp: true},
                body: body,
            })
        }
        const updateShopperContext = async () => {
            const _usid = await getUsidWhenReady()
            await updateShopperContextMutation({
                parameters: {usid: _usid, siteId: site.id, evaluateContextWithClientIp: true},
                body: {
                    customQualifiers: {
                        optAccepted: consentCategoryValue,
                    },
                },
            })
            void updateDnt(false)
            await getTokenWhenReady()
        }
        if ((!shopperContext && !isShopperContextLoading) || (shopperContext && Object.keys(shopperContext).length === 0)) {
            void createShopperContext()
        } else if (
            shopperContext &&
            Object.keys(shopperContext).length > 0 &&
            shopperContext.customQualifiers?.optAccepted !== '1' &&
            consentCategoryValue === '1'
        ) {
            void updateShopperContext()
        }
    }, [shopperContext, isShopperContextLoading])

    const handleFirstRender = () => {
        if (customer?.customerId && customer?.isRegistered) {
            firstRender.current = false
            refetchCustomer()
        }
    }

    /**
     * When the page changes analyticsUserData event must be triggered.
     * First render is handled separately because useCurrentCustomer does not fetch data properly in that case.
     */
    useEffect(() => {
        if (firstRender.current) {
            if (customer?.customerId && customer.isRegistered) {
                handleFirstRender()
                return
            }

            // Basket is no longer created eagerly, so a visitor without a cart has no basket and
            // no c_productsQuantity. Only wait for the customer here; the cart count defaults to 0.
            if (!customer) {
                return
            }

            firstRender.current = false
        }

        if (customer) {
            const searchParams = new URLSearchParams(location.search)
            const currentTemplate = searchParams.get('c_template')
            const currentPath = location.pathname

            let previousTemplate: string | null = null
            let previousPath: string | null = null

            if (previousPathname.current) {
                const [path, search] = previousPathname.current.split('?')
                previousPath = path
                if (search) {
                    const prevParams = new URLSearchParams(search)
                    previousTemplate = prevParams.get('c_template')
                }
            }

            if (previousPath !== currentPath || previousTemplate !== currentTemplate) {
                previousPathname.current = currentPath + (location.search ? `${location.search}` : '')

                analyticsUserData({
                    isLogged: customer.isRegistered,
                    clientId: customer.isRegistered ? customer.customerId : '',
                    hashId: customer.isRegistered && customer.email ? hashValueHex(customer.email.toLowerCase()) : '',
                    // Default to 0 when the visitor has no basket (basket is created lazily now).
                    basketProductsLength: basket?.c_productsQuantity ?? 0,
                })
            }
        }
    }, [location, customer, basket])

    return (
        <Box
            className={`sf-app safe-area-wrapper${hasDynamicBottomBar ? ' has-dynamic-bottom-bar' : ''}${
                hasDynamicBottomBarFirefox ? ' has-dynamic-bottom-bar-firefox' : ''
            }`}
        >
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet htmlAttributes={{lang: targetLocale.split('-')[0].replace(/^ch$/, 'zh')}}>
                    {/*
                     * `viewport-fit=cover` stabilizes the iOS 26 Safari viewport while the bottom
                     * "Liquid Glass" toolbar collapses/expands on scroll. Without it the viewport is
                     * shifted vertically as the bar animates, which makes full-height (100dvh) swiper
                     * slides resize mid-transition and the swiper→footer scroll sync jitter/vibrate.
                     * react-helmet dedupes meta by `name`, so this replaces the PWA Kit default (which
                     * cannot be overridden via ccExtensibility since the SDK imports _document through
                     * an internal relative path). The safe-area insets it exposes are restored by the
                     * `.safe-area-wrapper` container below so no content ends up under notch/home bar.
                     */}
                    <meta name="viewport" content="width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0, viewport-fit=cover" />
                    {ACTIVE_DATA_ENABLED && <script src={getAssetUrl('static/head-active_data.js')} id="headActiveData" type="text/javascript" />}
                    {parsedMetaTags}
                    {appPreferences?.isBaiduAnalysisEnabled && <meta name="applicable-device" content="pc,mobile" />}
                    {appPreferences?.isBaiduAnalysisEnabled && <meta name="baidu-site-verification" content={appPreferences.baiduSiteVerification} />}
                    {hasDynamicYieldConfig && <link rel="preconnect" href={dyHosts.cdn} />}
                    {hasDynamicYieldConfig && <link rel="preconnect" href={dyHosts.st} />}
                    {hasDynamicYieldConfig && <link rel="preconnect" href={dyHosts.rcom} />}
                    {hasDynamicYieldConfig && (
                        <script
                            type="text/javascript"
                            dangerouslySetInnerHTML={{
                                __html: `window.DY = window.DY || {}; window.DY.recommendationContext = ${JSON.stringify({})};`,
                            }}
                        />
                    )}
                    {hasDynamicYieldConfig && (
                        <script type="text/javascript" src={`${dyHosts.cdn}/api/${dynamicYieldInstanceId}/api_dynamic.js`} onLoad={registerDyReadyCallback} />
                    )}
                    {hasDynamicYieldConfig && (
                        <script type="text/javascript" src={`${dyHosts.cdn}/api/${dynamicYieldInstanceId}/api_static.js`} onLoad={registerDyReadyCallback} />
                    )}
                </Helmet>

                <IntlProvider
                    onError={(err) => {
                        if (!messages) {
                            // During the ssr prepass phase the messages object has not loaded, so we can suppress
                            // errors during this time.
                            return
                        }

                        if (String(err.code) === 'MISSING_TRANSLATION') {
                            // NOTE: Remove the console error for missing translations during development,
                            // as we knew translations would be added later.
                            logger.warn('Missing translation', {
                                namespace: 'App.IntlProvider',
                                additionalProperties: {
                                    errorMessage: err.message,
                                },
                            })
                            return
                        }
                        throw err
                    }}
                    locale={intlLocale}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <CookiesProvider>
                        <GoogleReCaptchaProvider reCaptchaKey={appPreferences?.apikeyv3_recaptcha}>
                            <CurrencyProvider currency={currency}>
                                <LoaderProvider>
                                    <GlobalConfigProvider appPreferences={appPreferences}>
                                        <AccessibilityProvider accessibilityPrefs={appPreferences?.ACCESSIBILITY_USABLENET_JS}>
                                            <DrawerProvider>
                                                <ModalProvider>
                                                    <Seo>
                                                        <meta name="theme-color" content={THEME_COLOR} />
                                                        <meta name="apple-mobile-web-app-title" content={DEFAULT_SITE_TITLE} />
                                                        <link
                                                            rel="apple-touch-icon"
                                                            sizes="180x180"
                                                            href={getAssetUrl('static/img/global/apple-touch-icon.png')}
                                                        />
                                                        <link
                                                            rel="icon"
                                                            type="image/png"
                                                            sizes="32x32"
                                                            href={getAssetUrl('static/img/global/favicon-32x32.png')}
                                                        />
                                                        <link
                                                            rel="icon"
                                                            type="image/png"
                                                            sizes="16x16"
                                                            href={getAssetUrl('static/img/global/favicon-16x16.png')}
                                                        />
                                                        <link rel="manifest" href={getAssetUrl('static/manifest.json')} />
                                                    </Seo>

                                                    {appPreferences?.pwaLiveChatScript !== undefined && (
                                                        <RawHtmlInjector htmlString={appPreferences.pwaLiveChatScript} />
                                                    )}

                                                    <HeaderProvider>
                                                        <Box id="app" display="flex" flexDirection="column" flex={1}>
                                                            <Header shopperContext={shopperContext} />

                                                            <SkipNavContent
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    flex: 1,
                                                                    outline: 0,
                                                                }}
                                                            >
                                                                <Box as="main" id="app-main" role="main" display="flex" flexDirection="column" flex="1">
                                                                    {children}
                                                                </Box>
                                                            </SkipNavContent>

                                                            <NewsletterModalSmall />
                                                            <Footer />

                                                            {/*
                                                             * iOS 26 Safari draws its bottom "Liquid Glass" toolbar as a translucent
                                                             * surface and samples the page content behind it to tint the bar, so the
                                                             * scrolling content shows through and flickers.
                                                             *
                                                             * This thin "shim" is pushed almost entirely off-screen (only ~5px stays in
                                                             * view, itself behind the toolbar) and painted with a solid, near-opaque
                                                             * color. Safari samples it as the bar's backdrop, so the bar renders solid
                                                             * instead of showing the content. Because it is only a few pixels tall and
                                                             * off the visible area, it never covers real content (swiper, sticky CTA).
                                                             *
                                                             * Scoped to WebKit/Safari via `@supports (-webkit-touch-callout: none)` and
                                                             * `pointer-events: none`, so it is inert on every other browser and cannot
                                                             * intercept taps. See the `safariBarShim` rule in globalStyles.
                                                             */}
                                                            <Box className="safari-bar-shim" aria-hidden="true" />
                                                        </Box>
                                                    </HeaderProvider>
                                                </ModalProvider>
                                            </DrawerProvider>
                                        </AccessibilityProvider>
                                    </GlobalConfigProvider>
                                </LoaderProvider>
                            </CurrencyProvider>
                        </GoogleReCaptchaProvider>
                    </CookiesProvider>
                </IntlProvider>
            </StorefrontPreview>
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node,
}

export default App
